import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Users, CreditCard, Database, FolderTree, Activity, FileStack, TrendingUp, Sparkles } from "lucide-react";

function formatBytes(b) {
  if (!b) return "0 B";
  const u = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let n = b;
  while (n >= 1024 && i < u.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(n < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className={`border ${accent ? "border-[#002FA7]" : "border-border"} bg-card p-5`}>
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase text-muted-foreground">{label}</span>
        <Icon className={`h-4 w-4 ${accent ? "text-[#002FA7]" : "text-muted-foreground"}`} />
      </div>
      <div className="mt-3 font-display text-3xl font-black tracking-tight">{value}</div>
      {sub && <div className="mt-1 font-mono text-[11px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    success: "bg-emerald-500",
    partial: "bg-amber-500",
    failed: "bg-destructive",
    running: "bg-blue-500",
    idle: "bg-muted-foreground",
  };
  return <span className={`inline-block h-1.5 w-1.5 ${map[status] || map.idle}`} />;
}

export default function AdminOverview() {
  const { t } = useI18n();
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/admin/overview").then(({ data }) => setD(data));
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  if (!d) {
    return <div className="font-mono text-sm text-muted-foreground">{t("common.loading")}</div>;
  }

  return (
    <div>
      <div className="font-mono text-xs uppercase tracking-wider text-[#002FA7]">// {t("admin.nav.overview")}</div>
      <h1 className="mt-2 font-display text-3xl font-black tracking-tight">{t("admin.title")}</h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("admin.subtitle")}</p>

      {/* KPIs */}
      <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label={t("admin.stats.users")} value={d.users.total} sub={`+${d.users.new_7d} ${t("admin.stats.new7d")}`} icon={Users} />
        <Stat label={t("admin.stats.paying")} value={d.users.paying} sub={`${d.users.trial} ${t("admin.stats.trial")}`} icon={Sparkles} />
        <Stat label={t("admin.stats.jobs")} value={d.jobs.total} sub={`${d.jobs.enabled} enabled`} icon={Database} />
        <Stat
          label={t("admin.stats.failedRuns")}
          value={d.jobs.failed_7d}
          sub={`${d.jobs.runs_total} total runs`}
          icon={Activity}
          accent={d.jobs.failed_7d > 0}
        />
      </div>

      {/* Revenue */}
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Stat label={t("admin.stats.revenueTotal")} value={`${d.revenue.total_eur.toFixed(2)}€`} icon={TrendingUp} accent />
        <Stat label={t("admin.stats.revenue30d")} value={`${d.revenue.last_30d_eur.toFixed(2)}€`} icon={TrendingUp} />
        <Stat label={t("admin.stats.revenue7d")} value={`${d.revenue.last_7d_eur.toFixed(2)}€`} icon={TrendingUp} />
      </div>

      {/* Plan distribution */}
      <section className="mt-10">
        <h2 className="font-display text-lg font-bold tracking-tight">{t("admin.stats.planDistribution")}</h2>
        <div className="mt-4 grid grid-cols-1 gap-px border border-border bg-border sm:grid-cols-3">
          {["free", "pro", "unlimited"].map((p) => (
            <div key={p} className="bg-card p-5">
              <div className="font-mono text-[11px] uppercase text-muted-foreground">{p}</div>
              <div className="mt-2 font-display text-2xl font-black tracking-tight">{d.users.plan_distribution?.[p] || 0}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Volume */}
      <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat label={t("admin.stats.accounts")} value={d.accounts.total} icon={FolderTree} />
        <Stat label={t("admin.stats.bytesTotal")} value={formatBytes(d.usage.bytes_total)} icon={FileStack} />
        <Stat label={t("admin.stats.filesTotal")} value={d.usage.files_total} icon={FileStack} />
        <Stat label={t("admin.stats.runs")} value={d.jobs.runs_total} icon={Activity} />
      </div>

      {/* Recent activity */}
      <section className="mt-10">
        <h2 className="font-display text-lg font-bold tracking-tight">{t("admin.stats.recentRuns")}</h2>
        <div className="mt-4 border border-border bg-card">
          {!d.recent_runs?.length && (
            <div className="p-6 font-mono text-sm text-muted-foreground">—</div>
          )}
          {d.recent_runs?.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-border px-4 py-3 last:border-0 text-sm">
              <div className="flex items-center gap-3">
                <StatusDot status={r.status} />
                <span className="font-mono text-xs">job::{r.job_id.slice(0, 8)}</span>
                <span className="font-mono text-[11px] text-muted-foreground">user::{r.user_id.slice(0, 8)}</span>
              </div>
              <div className="flex items-center gap-4 font-mono text-xs text-muted-foreground">
                <span>{r.files_copied}↑ · {r.files_failed}✗</span>
                <span>{formatBytes(r.bytes_transferred)}</span>
                <span className="hidden sm:inline">{new Date(r.started_at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
